import classNames from 'classnames';
import React, {
  ButtonHTMLAttributes,
  AnchorHTMLAttributes,
  ReactNode,
  forwardRef,
} from 'react';

export type ButtonStyleType =
  | 'primary'
  | 'secondary'
  | 'neutral'
  | 'neutral-light-and-blue'
  | 'cta';
export type ButtonVariant = 'filled' | 'outline' | 'text';
export type ButtonElementType = 'button' | 'a';
export type ButtonSize = 'regular' | 'small';

type ButtonBaseProps = {
  variant?: ButtonVariant;
  styleType?: ButtonStyleType;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  as?: ButtonElementType;
  className?: string;
  children?: ReactNode;
  disabled?: boolean;
  selected?: boolean;
  size?: ButtonSize;
};

type ButtonAsButtonProps = ButtonBaseProps & {
  as?: 'button';
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonBaseProps>;

type ButtonAsAnchorProps = ButtonBaseProps & {
  as: 'a';
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonBaseProps>;

export type ButtonProps = ButtonAsButtonProps | ButtonAsAnchorProps;

const classNameMap: {
  [K in ButtonVariant]: {
    [K in ButtonStyleType]: string;
  };
} = {
  filled: {
    primary: 'mhg-alpha-btn-primary',
    cta: 'mhg-alpha-btn-cta',
    secondary: 'mhg-alpha-btn-secondary',
    neutral: 'mhg-alpha-btn-neutral',
    'neutral-light-and-blue': 'mhg-alpha-btn-neutral-light-and-blue',
  },
  outline: {
    primary: 'mhg-alpha-btn-outline-primary',
    cta: 'mhg-alpha-btn-outline-cta',
    secondary: 'mhg-alpha-btn-outline-secondary',
    neutral: 'mhg-alpha-btn-outline-neutral',
    'neutral-light-and-blue': 'mhg-alpha-btn-outline-neutral-light-and-blue',
  },
  // text variant is the same for all style types
  text: {
    primary: 'mhg-alpha-btn-text',
    cta: 'mhg-alpha-btn-text',
    secondary: 'mhg-alpha-btn-text',
    neutral: 'mhg-alpha-btn-text',
    'neutral-light-and-blue': 'mhg-alpha-btn-text',
  },
};

const classNameMapForDisabled: { [K in ButtonVariant]: string } = {
  filled: 'mhg-alpha-btn-disabled',
  outline: 'mhg-alpha-btn-outline-disabled',
  text: 'mhg-alpha-btn-text-disabled',
};

const Button = forwardRef(function Button<
  T extends HTMLButtonElement | HTMLAnchorElement,
>(
  props: T extends HTMLButtonElement
    ? ButtonAsButtonProps
    : ButtonAsAnchorProps,
  ref: React.ForwardedRef<T>,
) {
  const {
    styleType = 'primary',
    variant = 'filled',
    disabled,
    className,
    children,
    startIcon,
    endIcon,
    selected,
    size = 'regular',
    as = 'button',
    ...rest
  } = props as ButtonProps;

  const btnClassName = disabled
    ? classNameMapForDisabled[variant]
    : classNameMap[variant][styleType];

  const sharedClassNames = classNames(
    btnClassName,
    {
      'pr-[--button-padding-regular-x-icon]': !!endIcon,
      'pl-[--button-padding-regular-x-icon]': !!startIcon,
      'mhg-alpha-btn-selected': !!selected,
      'mhg-alpha-btn-small': size === 'small',
    },
    className,
  );

  const iconSizeClass =
    size === 'small'
      ? 'size-[--button-icon-small] font-size-[--button-icon-small]'
      : 'size-[--button-icon-regular] font-size-[--button-icon-regular]';

  const iconStartElement = startIcon && (
    <span className={`flex items-center justify-center ${iconSizeClass}`}>
      {startIcon}
    </span>
  );

  const iconEndElement = endIcon && (
    <span className={`flex items-center justify-center ${iconSizeClass}`}>
      {endIcon}
    </span>
  );

  if (as === 'a') {
    return (
      <a
        className={sharedClassNames}
        ref={ref as React.Ref<HTMLAnchorElement>}
        {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)}
        aria-disabled={disabled}
      >
        {iconStartElement}
        {children}
        {iconEndElement}
      </a>
    );
  }

  return (
    <button
      type="button"
      className={sharedClassNames}
      disabled={disabled}
      ref={ref as React.Ref<HTMLButtonElement>}
      {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {iconStartElement}
      {children}
      {iconEndElement}
    </button>
  );
}) as <T extends HTMLButtonElement | HTMLAnchorElement>(
  props: T extends HTMLButtonElement
    ? ButtonAsButtonProps
    : ButtonAsAnchorProps & {
        ref?: React.ForwardedRef<T>;
      },
) => JSX.Element;

export default Button;
